import { useState, useMemo } from 'react'
import { AlertTriangle, Crown, MapPin, List, User, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useCandidateWithDetails } from '@/hooks/useCandidateWithDetails'
import { usePartyLeader } from '@/hooks/usePartyLeader'
import { useAssignCandidateRole } from '@/hooks/useAssignCandidateRole'
import { DistrictSelector } from './DistrictSelector'
import { CandidateRoleBadge } from './CandidateRoleBadge'
import type { CandidateRole, CandidateWithDetails } from '@/types'

interface RoleAssignmentModalProps {
  candidateId: string | null
  partyId: string
  nationId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

// Role configuration for MVP
const ROLE_CONFIG: {
  role: CandidateRole
  label: string
  icon: React.ReactNode
  description: string
  available: boolean
}[] = [
  {
    role: 'none',
    label: 'Unassigned',
    icon: <User className="h-4 w-4" />,
    description: 'No active role. Candidate is available for assignment.',
    available: true,
  },
  {
    role: 'party_leader',
    label: 'Party Leader',
    icon: <Crown className="h-4 w-4 text-amber-500" />,
    description:
      'The face of your party. Required for some actions. Only one per party.',
    available: true,
  },
  {
    role: 'regional_candidate',
    label: 'Regional Candidate',
    icon: <MapPin className="h-4 w-4 text-blue-500" />,
    description:
      'Runs for election in a specific district. Their personal appeal affects votes there.',
    available: true,
  },
  {
    role: 'list_candidate',
    label: 'List Candidate',
    icon: <List className="h-4 w-4 text-purple-500" />,
    description: 'On the party list for proportional representation seats.',
    available: true,
  },
  {
    role: 'minister',
    label: 'Minister',
    icon: <User className="h-4 w-4 text-gray-400" />,
    description: 'Serves in the cabinet. (Available when in government)',
    available: false,
  },
  {
    role: 'chair',
    label: 'Chair',
    icon: <User className="h-4 w-4 text-gray-400" />,
    description: 'Committee chair position. (Future feature)',
    available: false,
  },
]

function getRoleConfig(role: CandidateRole) {
  return ROLE_CONFIG.find((r) => r.role === role) ?? ROLE_CONFIG[0]
}

interface RoleAssignmentFormProps {
  candidate: CandidateWithDetails
  partyId: string
  nationId: string
  onClose: () => void
  onSuccess?: () => void
}

function RoleAssignmentForm({
  candidate,
  partyId,
  nationId,
  onClose,
  onSuccess,
}: RoleAssignmentFormProps) {
  const { leader: currentLeader } = usePartyLeader(partyId)

  const { assignRoleAsync, isAssigning, removeRoleAsync, isRemoving } =
    useAssignCandidateRole(candidate.id, partyId)

  // Initialize state from candidate's current values
  const [selectedRole, setSelectedRole] = useState<CandidateRole>(candidate.role)
  const [selectedDistrictId, setSelectedDistrictId] = useState<string | undefined>(
    candidate.assigned_district_id ?? undefined
  )
  const [error, setError] = useState<string | null>(null)

  const isSaving = isAssigning || isRemoving

  // Check if a different candidate is currently the party leader
  const existingLeaderWarning = useMemo(() => {
    if (selectedRole !== 'party_leader') return null
    if (!currentLeader) return null
    if (currentLeader.id === candidate.id) return null
    return currentLeader
  }, [selectedRole, currentLeader, candidate.id])

  // Check if role has changed
  const hasChanges = useMemo(() => {
    if (selectedRole !== candidate.role) return true
    if (
      selectedRole === 'regional_candidate' &&
      selectedDistrictId !== (candidate.assigned_district_id ?? undefined)
    ) {
      return true
    }
    return false
  }, [candidate, selectedRole, selectedDistrictId])

  // Validate assignment
  const validationError = useMemo(() => {
    if (selectedRole === 'regional_candidate' && !selectedDistrictId) {
      return 'Please select a district for the regional candidate.'
    }
    return null
  }, [selectedRole, selectedDistrictId])

  const canSubmit = hasChanges && !validationError && !isSaving

  const handleRoleChange = (role: CandidateRole) => {
    setSelectedRole(role)
    setError(null)
    // Clear district selection if not regional candidate
    if (role !== 'regional_candidate') {
      setSelectedDistrictId(undefined)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    setError(null)

    try {
      if (selectedRole === 'none') {
        await removeRoleAsync()
      } else {
        await assignRoleAsync({
          role: selectedRole,
          districtId:
            selectedRole === 'regional_candidate' ? selectedDistrictId : undefined,
        })
      }

      onSuccess?.()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign role')
    }
  }

  const handleClose = () => {
    if (!isSaving) {
      onClose()
    }
  }

  return (
    <>
      <div className="space-y-6">
        {/* Candidate Info */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Candidate</p>
          <p className="text-lg font-semibold">{candidate.name}</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Current Role:</span>
            <CandidateRoleBadge
              role={candidate.role}
              districtName={candidate.district?.name}
            />
          </div>
        </div>

        {/* Role Selector */}
        <div className="space-y-2">
          <Label>New Role</Label>
          <Select value={selectedRole} onValueChange={handleRoleChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select a role...">
                {selectedRole && (
                  <span className="flex items-center gap-2">
                    {getRoleConfig(selectedRole).icon}
                    {getRoleConfig(selectedRole).label}
                  </span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ROLE_CONFIG.filter((r) => r.available).map((roleConfig) => (
                <SelectItem key={roleConfig.role} value={roleConfig.role}>
                  <div className="flex items-center gap-2">
                    {roleConfig.icon}
                    <span>{roleConfig.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedRole && (
            <p className="text-sm text-muted-foreground">
              {getRoleConfig(selectedRole).description}
            </p>
          )}
        </div>

        {/* District Selector (for regional candidate) */}
        {selectedRole === 'regional_candidate' && (
          <div className="space-y-2">
            <Label>Assign to District</Label>
            <DistrictSelector
              nationId={nationId}
              partyId={partyId}
              selectedDistrictId={selectedDistrictId}
              onChange={setSelectedDistrictId}
              currentCandidateId={candidate.id}
            />
          </div>
        )}

        {/* Party Leader Warning */}
        {existingLeaderWarning && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm">
              <span className="font-medium">{existingLeaderWarning.name}</span> is
              currently the Party Leader. They will be set to Unassigned if you proceed.
            </p>
          </div>
        )}

        {/* Validation Error */}
        {validationError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm">{validationError}</p>
          </div>
        )}

        {/* Submit Error */}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Assign Role
        </Button>
      </DialogFooter>
    </>
  )
}

export function RoleAssignmentModal({
  candidateId,
  partyId,
  nationId,
  isOpen,
  onClose,
  onSuccess,
}: RoleAssignmentModalProps) {
  const { candidate, isLoading, error } = useCandidateWithDetails(
    isOpen ? candidateId ?? undefined : undefined
  )

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Role</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        ) : error ? (
          <div className="flex h-40 items-center justify-center">
            <div className="text-destructive">Failed to load candidate</div>
          </div>
        ) : candidate ? (
          <RoleAssignmentForm
            key={candidate.id}
            candidate={candidate}
            partyId={partyId}
            nationId={nationId}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        ) : (
          <div className="flex h-40 items-center justify-center">
            <div className="text-destructive">Candidate not found</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
